/**
 * Global fetch interceptor that detects 401/403 responses from the backend
 * and redirects to /login when the JWT has expired or is invalid.
 *
 * This prevents the "silent failure" problem where API calls fail with 403
 * but the user sees a broken page with no explanation.
 *
 * Install once at app startup:
 *   import { installFetchInterceptor } from './utils/fetchInterceptor';
 *   installFetchInterceptor();
 *
 * How it works:
 * - Monkey-patches window.fetch to wrap every response
 * - If a response is 401 or 403 AND the URL is our backend API, it clears
 *   storage and redirects to /login
 * - Auth endpoints (/auth/login, /auth/forgot-password, etc.) are excluded
 *   so that login errors show normally
 * - A debounce flag prevents multiple redirects from racing
 */

const AUTH_PATHS = ['/auth/login', '/auth/forgot-password', '/auth/resetPassword', '/auth/getOTPfromDB', '/auth/fetchRelatedHospitals'];

let isRedirecting = false;

export function installFetchInterceptor() {
  const originalFetch = window.fetch;

  window.fetch = async function (...args) {
    const response = await originalFetch.apply(this, args);

    // Only intercept our own API calls, not PACS/external requests
    const url = typeof args[0] === 'string' ? args[0] : args[0]?.url || '';
    const isAuthEndpoint = AUTH_PATHS.some(path => url.includes(path));
    // DICOMweb now goes through our backend proxy. A 403 there means "this study/clinic
    // is out of scope" or a per-request auth hiccup — never a reason to end the whole
    // app session, so it must not trigger the logout-on-expiry path below.
    const isDicomWeb = url.includes('/dicomweb/');

    if (
      (response.status === 401 || response.status === 403) &&
      !isAuthEndpoint &&
      !isDicomWeb &&
      !isRedirecting
    ) {
      // Check if this looks like a token-expired response from our backend
      // (not a role-based 403 from requireRole which returns a JSON body)
      try {
        const cloned = response.clone();
        const body = await cloned.json();
        const msg = (body?.msg || '').toLowerCase();

        if (
          msg.includes('unauthorized') ||
          msg.includes('token') ||
          msg.includes('jwt') ||
          msg.includes('expired')
        ) {
          isRedirecting = true;

          // Clear session data
          const APP_STORAGE_KEYS = [
            'token', 'role', 'name', 'email', 'loggedinEmail',
            'userId', 'hospitalId', 'hospitalName', 'type',
            'pacsDetails', 'pacs_access_token', 'pacs_override',
          ];
          for (const key of APP_STORAGE_KEYS) {
            localStorage.removeItem(key);
            sessionStorage.removeItem(key);
          }

          // Redirect to login — use replace so back button doesn't go to broken page
          window.location.replace('/login');
          return response;
        }
      } catch {
        // Response wasn't JSON or clone failed — don't intercept
      }
    }

    return response;
  };
}
